const createButton = (label, variant = 'secondary') => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `linkit-control linkit-control-md linkit-control-${variant}`;
  button.textContent = label;
  return button;
};

const createShell = ({ title, description, tone = 'default' }) => {
  const overlay = document.createElement('div');
  overlay.className = 'linkit-dialog-overlay linkit-imperative-overlay';

  const dialog = document.createElement('section');
  dialog.className = `linkit-dialog linkit-dialog-${tone} linkit-imperative-dialog`;
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');

  const header = document.createElement('div');
  header.className = 'linkit-dialog-header';

  const heading = document.createElement('h2');
  heading.textContent = title;
  const copy = document.createElement('p');
  copy.textContent = description;

  header.append(heading, copy);
  dialog.append(header);
  document.body.append(overlay, dialog);
  return { overlay, dialog };
};

const trapFocus = (dialog, event) => {
  if (event.key !== 'Tab') return;
  const controls = [...dialog.querySelectorAll('button, input, textarea, select, [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.disabled);
  if (!controls.length) return;
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

export function confirmAction({
  title = 'Confirm this action',
  description,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  tone = 'danger',
} = {}) {
  if (typeof document === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    const activeElement = document.activeElement;
    const { overlay, dialog } = createShell({ title, description, tone });
    const footer = document.createElement('div');
    footer.className = 'linkit-dialog-footer';
    const cancel = createButton(cancelLabel, 'secondary');
    const confirm = createButton(confirmLabel, tone === 'danger' ? 'danger' : 'primary');
    footer.append(cancel, confirm);
    dialog.append(footer);

    const finish = (result) => {
      document.removeEventListener('keydown', onKeyDown);
      overlay.remove();
      dialog.remove();
      activeElement?.focus?.();
      resolve(result);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') finish(false);
      trapFocus(dialog, event);
    };

    cancel.addEventListener('click', () => finish(false));
    confirm.addEventListener('click', () => finish(true));
    overlay.addEventListener('click', () => finish(false));
    document.addEventListener('keydown', onKeyDown);
    requestAnimationFrame(() => cancel.focus());
  });
}

export function promptAction({
  title = 'Enter a value',
  description,
  label = 'Value',
  placeholder = '',
  submitLabel = 'Continue',
  cancelLabel = 'Cancel',
  type = 'text',
} = {}) {
  if (typeof document === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {
    const activeElement = document.activeElement;
    const { overlay, dialog } = createShell({ title, description });
    const body = document.createElement('div');
    body.className = 'linkit-dialog-body';
    const field = document.createElement('label');
    field.className = 'linkit-field';
    const fieldLabel = document.createElement('span');
    fieldLabel.textContent = label;
    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    input.className = 'linkit-field-control';
    field.append(fieldLabel, input);
    body.append(field);

    const footer = document.createElement('div');
    footer.className = 'linkit-dialog-footer';
    const cancel = createButton(cancelLabel, 'secondary');
    const submit = createButton(submitLabel, 'primary');
    footer.append(cancel, submit);
    dialog.append(body, footer);

    const finish = (result) => {
      document.removeEventListener('keydown', onKeyDown);
      overlay.remove();
      dialog.remove();
      activeElement?.focus?.();
      resolve(result);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') finish(null);
      if (event.key === 'Enter') finish(input.value);
      trapFocus(dialog, event);
    };

    cancel.addEventListener('click', () => finish(null));
    submit.addEventListener('click', () => finish(input.value));
    overlay.addEventListener('click', () => finish(null));
    document.addEventListener('keydown', onKeyDown);
    requestAnimationFrame(() => input.focus());
  });
}
